import React, { useCallback } from 'react';
import { Button, Card, Col, Row, Table, Tag, Tooltip as AntdTooltip, message } from 'antd';
import { DownloadOutlined, FilterOutlined, PrinterOutlined } from '@ant-design/icons';
import html2pdf from 'html2pdf.js';
import { DISPLAY_DATE_FORMAT, VIEW_MODE } from './LifeCycleAnalyticsConstants';
import { EmptyState } from '../empty_state/EmptyState';

const PDF_EXPORT_ROOT_SELECTOR = '.lifeCycleAnalyticsOverviewPage';

const PrioritySummaryCard = ({
	loading,
	totalCompletedWorkOrders,
	prioritySummaryCards,
	tableDataSource,
	expandedDropdownData,
	priorityTableColumns,
	viewMode,
	hasActiveFilters,
	activeFilters,
	problemTypeNames,
	handleArrowClick,
	handleRowClick,
	openFilterModal,
	exportPrioritySummaryCsv,
	removeProblemTypeFilter,
	handleRemoveCreatedAtFilter,
	handleRemoveCompletedAtFilter,
}) => {
	const handleExportPdf = useCallback(async () => {
		const root = document.querySelector(PDF_EXPORT_ROOT_SELECTOR);
		if (!root) {
			message.error('Could not find page content to export.');
			return;
		}

		const hidePdfLoadingMessage = message.loading('Generating PDF…', 0);

		const filename = 'life-cycle-analytics-' + new Date().toISOString().slice(0, 10) + '.pdf';

		// Inject a stylesheet into the live document BEFORE html2canvas clones it.
		// html2canvas resolves styles via window.getComputedStyle against the MAIN
		// document's CSS — modifications inside onclone are invisible to it.  A
		// <style> injected here is copied into the iframe by html2canvas's own
		// createStyleClone, so the rules are live when computed styles are resolved.
		const woPerfPdfStyle = document.createElement('style');
		woPerfPdfStyle.setAttribute('data-pdf-override', 'wo-perf');
		woPerfPdfStyle.textContent = [
			// Force the chart/table row to stack vertically.
			'.woPerformanceChartRow.ant-row{display:block!important;margin:0!important;width:100%!important;}',
			// Let each column expand to full width.
			'.woPerformanceChartRow.ant-row>.ant-col{display:block!important;max-width:100%!important;width:100%!important;}',
			// Chart col: remove the right divider.
			'.woPerformanceChartRow.ant-row>.ant-col:first-child{border-right:none!important;padding-bottom:16px!important;}',
			// Table col: force onto next page, top separator only.
			'.woPerformanceChartRow.ant-row>.ant-col+.ant-col{page-break-before:always!important;break-before:page!important;border-top:1px solid #e5e5e5!important;border-right:none!important;border-left:none!important;border-bottom:none!important;padding-top:16px!important;}',
		].join('');
		document.head.appendChild(woPerfPdfStyle);

		try {
			await html2pdf()
				.from(root)
				.set({
					margin: 10,
					filename: filename,
					image: { type: 'jpeg', quality: 0.95 },
					html2canvas: {
						scale: 2,
						useCORS: true,
						logging: false,
						letterRendering: true,
						// html2canvas reads <canvas> pixel data directly and does NOT apply
						// the parent's CSS transform to it — so scaling a container that
						// holds a BizCharts canvas produces a scaled card border/background
						// but a full-size chart inside. The fix: in onclone, swap every
						// chart canvas for an <img> carrying the same pixel data (via
						// toDataURL on the live DOM canvas). html2canvas DOES honour CSS
						// transforms on <img> elements, so the row-level scale then works.
						onclone: (clonedDoc: Document) => {
							const CHART_SCALE = 1;

							// 0. Priority summary toolbar: hide only in the clone so Filter / Print / Download
							//    stay visible on screen while the PDF is generated (html2canvas uses screen media).
							(
								Array.from(
									clonedDoc.querySelectorAll('.life-cycle-priority-summary-pdf-toolbar button')
								) as HTMLElement[]
							).forEach((btn) => {
								btn.style.display = 'none';
							});
							(
								Array.from(
									clonedDoc.querySelectorAll('.lifeCycleSummaryCard .ant-tag-close-icon')
								) as HTMLElement[]
							).forEach((el) => {
								el.style.display = 'none';
							});

							// 1. Canvas → img: html2canvas reads <canvas> raw pixel data and
							//    ignores parent CSS transforms/zoom on canvas elements. Replacing
							//    with <img> (sourced from the live canvas) makes zoom work correctly.
							//    This applies to both the metric chart rows AND the WO Performance
							//    scatter chart — without the conversion the WO Performance canvas
							//    renders at its native pixel size (ignoring the scale:2 layout
							//    multiplier) and overflows into the adjacent table column.
							const liveCanvases = Array.from(
								document.querySelectorAll('.lifeCycleAnalyticsChartsRow canvas')
							) as HTMLCanvasElement[];

							(
								Array.from(
									clonedDoc.querySelectorAll('.lifeCycleAnalyticsChartsRow canvas')
								) as HTMLCanvasElement[]
							).forEach((clonedCanvas, i) => {
								if (i >= liveCanvases.length) {
									return;
								}
								try {
									const img = clonedDoc.createElement('img') as HTMLImageElement;
									img.src = liveCanvases[i].toDataURL('image/png');
									img.style.display = 'block';
									img.style.width = '100%';
									img.style.height =
										liveCanvases[i].style.height || liveCanvases[i].offsetHeight + 'px';
									if (clonedCanvas.parentNode) {
										clonedCanvas.parentNode.replaceChild(img, clonedCanvas);
									}
								} catch (_e) {
									// Cross-origin tainted canvas — leave as-is.
								}
							});

							// 1b. Same canvas → img fix for the WO Performance scatter chart.
							//     Using a slightly reduced width (88%) so the chart image stays
							//     clearly within its column and does not overlap the table.
							const liveWoPerfCanvases = Array.from(
								document.querySelectorAll('.woPerformanceChartRow canvas')
							) as HTMLCanvasElement[];

							(
								Array.from(
									clonedDoc.querySelectorAll('.woPerformanceChartRow canvas')
								) as HTMLCanvasElement[]
							).forEach((clonedCanvas, i) => {
								if (i >= liveWoPerfCanvases.length) {
									return;
								}
								try {
									const img = clonedDoc.createElement('img') as HTMLImageElement;
									img.src = liveWoPerfCanvases[i].toDataURL('image/png');
									img.style.display = 'block';
									img.style.width = '100%';
									img.style.height =
										liveWoPerfCanvases[i].style.height || liveWoPerfCanvases[i].offsetHeight + 'px';
									if (clonedCanvas.parentNode) {
										clonedCanvas.parentNode.replaceChild(img, clonedCanvas);
									}
								} catch (_e) {
									// Cross-origin tainted canvas — leave as-is.
								}
							});

							// 2. Hide download buttons — print:!hidden is a CSS print-media
							//    class that html2canvas ignores (it captures screen context).
							(
								Array.from(
									clonedDoc.querySelectorAll('.lifeCycleAnalyticsChartsRow .ant-card-extra button')
								) as HTMLElement[]
							).forEach((btn) => {
								btn.style.display = 'none';
							});

							// 3. Mark each chart-row wrapper so html2pdf never splits a row
							//    across pages — if it doesn't fit on the current page it is
							//    pushed to the next page in its entirety.  A small paddingTop
							//    is added so that when a wrapper IS pushed to a new page the
							//    card's top border is not clipped at the page boundary.
							(
								Array.from(
									clonedDoc.querySelectorAll('.lifeCycleAnalyticsChartsRow')
								) as HTMLElement[]
							).forEach((row) => {
								const wrapper = row.parentElement;
								if (wrapper) {
									wrapper.style.pageBreakInside = 'avoid';
									wrapper.style.breakInside = 'avoid';
									wrapper.style.paddingTop = '8px';
								}
							});

							// 4. Add top padding to the WO Performance wrapper so the card's
							//    top border is not clipped when it starts at the top of a new
							//    page.  The actual page-break-before is declared in the
							//    pagebreak.before option below (that runs before html2canvas)
							//    so we only need the padding here.
							const woWrapper = clonedDoc.querySelector(
								'.woPerformanceSectionWrapper'
							) as HTMLElement;
							if (woWrapper) {
								woWrapper.style.paddingTop = '8px';
							}
						},
					},
					jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
					pagebreak: {
						mode: ['css', 'legacy'],
						avoid: '.lifeCycleAnalyticsChartsRow',
						// Force WO Performance to always start on a new page.  Using
						// pagebreak.before is reliable because html2pdf processes it
						// before html2canvas fires — unlike onclone inline styles which
						// arrive too late for the page-break scanner to see.
						before: '.woPerformanceSectionWrapper',
					},
				})
				.save();
		} catch (_err) {
			message.error('Failed to generate PDF. Try again or use a smaller date range.');
		} finally {
			// Always remove the injected override style regardless of success/failure.
			if (woPerfPdfStyle.parentNode) {
				woPerfPdfStyle.parentNode.removeChild(woPerfPdfStyle);
			}
			hidePdfLoadingMessage();
		}
	}, []);

	return (
		<Row gutter={12} className="-mx-1.5 my-3">
			<Col span={24}>
				<Card className="lifeCycleSummaryCard">
					<div className="life-cycle-priority-summary-pdf-toolbar flex h-8 w-full items-center justify-between">
						<div className="flex h-8 items-center gap-2">
							<div className="h4medium">Completed Work Orders: {totalCompletedWorkOrders}</div>
							<Button
								className="print:!hidden"
								icon={<FilterOutlined />}
								onClick={openFilterModal}
							/>
						</div>
						<div className="flex items-center gap-2">
							<AntdTooltip title="Print">
								<Button
									className="print:!hidden"
									icon={<PrinterOutlined />}
									onClick={handleExportPdf}
								/>
							</AntdTooltip>
							<AntdTooltip title="Download">
								<Button
									className="print:!hidden"
									icon={<DownloadOutlined />}
									onClick={exportPrioritySummaryCsv}
								/>
							</AntdTooltip>
						</div>
					</div>
					{hasActiveFilters && (
						<div className="mb-4 mt-2.5 [&_.ant-tag-close-icon]:print:!hidden">
							{activeFilters.problemTypeIds.length > 0 && (
								<Tag closable onClose={removeProblemTypeFilter}>
									Problem Type:{' '}
									{problemTypeNames || `${activeFilters.problemTypeIds.length} selected`}
								</Tag>
							)}
							{activeFilters.createdAt && activeFilters.createdAt[0] && activeFilters.createdAt[1] && (
								<Tag closable onClose={handleRemoveCreatedAtFilter}>
									Created between: {activeFilters.createdAt[0].format(DISPLAY_DATE_FORMAT)} to{' '}
									{activeFilters.createdAt[1].format(DISPLAY_DATE_FORMAT)}
								</Tag>
							)}
							{activeFilters.completedAt &&
								activeFilters.completedAt[0] &&
								activeFilters.completedAt[1] && (
									<Tag closable onClose={handleRemoveCompletedAtFilter}>
										Completed between: {activeFilters.completedAt[0].format(DISPLAY_DATE_FORMAT)} to{' '}
										{activeFilters.completedAt[1].format(DISPLAY_DATE_FORMAT)}
									</Tag>
								)}
						</div>
					)}
					{!loading && totalCompletedWorkOrders === 0 ? (
						<EmptyState
							height={200}
							headline={'No completed work orders found'}
							body={
								<div className="max-w-[488px] text-center">
									There are no completed work orders for the selected filters. Try adjusting your
									filters or expanding the date range.
								</div>
							}
						/>
					) : (
						<>
							<div className="my-3 flex h-[25px] w-full gap-0.5 overflow-hidden rounded-[5px] bg-priority-bar-track">
								{prioritySummaryCards.map((item, index, arr) => (
									<AntdTooltip
										key={item.woPriorityId}
										overlayClassName="lifeCycleProgressTooltip"
										title={
											<div>
												<span
													className="inline-block whitespace-nowrap rounded-2xl px-3 py-1 align-middle text-sm font-medium leading-[22px]"
													style={{
														color: item.color,
														backgroundColor: item.bgColor,
													}}
												>
													{item.woPriorityName}
												</span>
												<div className="mt-1.5 whitespace-nowrap text-sm text-black/65">
													<strong className="text-black/85">{item.completedWoCount}</strong>{' '}
													Completed Work Orders
												</div>
											</div>
										}
									>
										<div
											style={{
												flexGrow: item.share,
												flexShrink: 1,
												flexBasis: 0,
												border: `1px solid ${item.color}`,
												backgroundColor: item.bgColor,
												cursor: 'pointer',
												borderRadius:
													index === 0 && arr.length === 1
														? '5px'
														: index === 0
														? '5px 0 0 5px'
														: index === arr.length - 1
														? '0 5px 5px 0'
														: '0',
											}}
											className="h-full min-w-1"
											onClick={() => handleArrowClick(item.woPriorityId)}
										/>
									</AntdTooltip>
								))}
							</div>
							<div className="lifeCyclePriorityTable">
								<Table
									columns={priorityTableColumns}
									dataSource={tableDataSource}
									pagination={false}
									size="small"
									tableLayout="fixed"
									rowKey={(row) => `${row.woPriorityId}`}
									onRow={handleRowClick}
								/>
								{viewMode === VIEW_MODE.EXPANDED && expandedDropdownData.length > 0 && (
									<div className="lifeCyclePriorityDropdown print:!static print:!z-auto print:!shadow-none">
										<Table
											columns={priorityTableColumns}
											dataSource={expandedDropdownData}
											pagination={false}
											size="small"
											showHeader={false}
											tableLayout="fixed"
											rowKey={(row) => `${row.woPriorityId}`}
											onRow={handleRowClick}
										/>
									</div>
								)}
							</div>
						</>
					)}
				</Card>
			</Col>
			{viewMode === VIEW_MODE.ALL && (
				<Col span={24} className="bodyregular px-4 py-3 text-center text-character-secondary">
					Changes shown are measured against previous period
				</Col>
			)}
		</Row>
	);
};

export default PrioritySummaryCard;
